// Central template service for global template management

const TEMPLATES_KEY = 'quote_templates';
const DEFAULT_TEMPLATES_INITIALIZED_KEY = 'default_templates_initialized';

// Default global templates (from QuoteSectionEditor)
const DEFAULT_TEMPLATES = [
  {
    id: 'default-zahlungsbedingungen',
    title: 'Zahlungsbedingungen',
    category: 'Standard',
    content: '30 Tage netto nach Rechnungsstellung.\nBei Aufträgen über €5.000 werden 50% Anzahlung bei Auftragserteilung fällig.',
    type: 'terms',
    isDefault: true
  },
  {
    id: 'default-lieferzeit',
    title: 'Lieferzeit',
    category: 'Standard',
    content: '4-6 Wochen nach Auftragserteilung und Erhalt aller benötigten Unterlagen.\nBei umfangreichen Projekten kann sich die Lieferzeit entsprechend verlängern.',
    type: 'delivery',
    isDefault: true
  },
  {
    id: 'default-gewaehrleistung',
    title: 'Gewährleistung',
    category: 'Standard',
    content: '12 Monate Gewährleistung auf alle Leistungen.\nKleine Anpassungen und Fehlerbehebungen sind in den ersten 30 Tagen kostenfrei.',
    type: 'warranty',
    isDefault: true
  },
  {
    id: 'default-projektablauf',
    title: 'Projektablauf',
    category: 'Standard',
    content: '1. Briefing und Konzepterstellung\n2. Design-Entwürfe und Abstimmung\n3. Technische Umsetzung\n4. Testing und Qualitätskontrolle\n5. Go-Live und Übergabe',
    type: 'process',
    isDefault: true
  },
  {
    id: 'default-inkludierte-leistungen',
    title: 'Inkludierte Leistungen',
    category: 'Standard',
    content: '✓ Konzepterstellung und Beratung\n✓ Responsive Design für alle Geräte\n✓ Suchmaschinenoptimierung (Basis)\n✓ 2 Wochen kostenfreier Support nach Go-Live',
    type: 'included',
    isDefault: true
  }
];

class TemplateService {
  // Initialize default templates on first use
  static initializeDefaultTemplates() {
    const initialized = localStorage.getItem(DEFAULT_TEMPLATES_INITIALIZED_KEY);
    if (!initialized) {
      // Get existing templates WITHOUT recursion
      let existing = [];
      try {
        const stored = localStorage.getItem(TEMPLATES_KEY);
        existing = stored ? JSON.parse(stored) : [];
      } catch (error) {
        console.error('Error loading existing templates:', error);
      }
      
      const merged = [...existing];
      
      DEFAULT_TEMPLATES.forEach(template => {
        if (!merged.find(t => t.id === template.id)) {
          merged.push({
            ...template,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          });
        }
      });
      
      this.saveTemplates(merged);
      localStorage.setItem(DEFAULT_TEMPLATES_INITIALIZED_KEY, 'true');
    }
  }
  
  // Get all templates
  static getTemplates() {
    try {
      // Initialize defaults on first call
      this.initializeDefaultTemplates();
      
      const stored = localStorage.getItem(TEMPLATES_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error loading templates:', error);
      return [];
    }
  }

  // Save templates
  static saveTemplates(templates) {
    try {
      localStorage.setItem(TEMPLATES_KEY, JSON.stringify(templates));
      // Dispatch custom event for synchronization
      window.dispatchEvent(new CustomEvent('templatesUpdated', { detail: templates }));
      return true;
    } catch (error) {
      console.error('Error saving templates:', error);
      return false;
    }
  }

  // Add single template
  static addTemplate(template) {
    const templates = this.getTemplates();
    const newTemplate = {
      ...template,
      id: template.id || Date.now().toString(),
      createdAt: template.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    templates.push(newTemplate);
    this.saveTemplates(templates);
    return newTemplate;
  }

  // Update template
  static updateTemplate(id, updates) {
    const templates = this.getTemplates();
    const index = templates.findIndex(t => t.id === id);
    if (index === -1) return null;
    
    templates[index] = {
      ...templates[index],
      ...updates,
      updatedAt: new Date().toISOString()
    };
    this.saveTemplates(templates);
    return templates[index];
  }

  // Delete template
  static deleteTemplate(id) {
    const templates = this.getTemplates();
    const filtered = templates.filter(t => t.id !== id);
    this.saveTemplates(filtered);
    return true;
  }

  // Get template by ID
  static getTemplate(id) {
    const templates = this.getTemplates();
    return templates.find(t => t.id === id);
  }

  // Get templates by type
  static getTemplatesByType(type) {
    const templates = this.getTemplates();
    return templates.filter(t => t.type === type);
  }

  // Subscribe to template changes
  static subscribe(callback) {
    const handler = (event) => callback(event.detail);
    window.addEventListener('templatesUpdated', handler);
    return () => window.removeEventListener('templatesUpdated', handler);
  }

  // Export templates
  static exportTemplates() {
    const templates = this.getTemplates();
    const dataStr = JSON.stringify(templates, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `vorlagen-export-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    URL.revokeObjectURL(url);
  }

  // Import templates
  static importTemplates(file, merge = true) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const imported = JSON.parse(e.target.result);
          if (!Array.isArray(imported)) {
            reject(new Error('Invalid file format'));
            return;
          }

          if (merge) {
            const existing = this.getTemplates();
            const merged = [...existing];
            imported.forEach(template => {
              if (!merged.find(t => t.id === template.id)) {
                merged.push(template);
              }
            });
            this.saveTemplates(merged);
            resolve(imported.length);
          } else {
            this.saveTemplates(imported);
            resolve(imported.length);
          }
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = reject;
      reader.readAsText(file);
    });
  }
}

export default TemplateService;
